import "./index.scss";
import { useState } from "react";
import Topbar from "../../components/Topbar";
import Footer from "../../components/Footer";
import CameraModal from "./Components/CemeraModal";
import MobileIcon from "../../assets/icon/mobile-green.svg";
import ShieldIcon from "../../assets/icon/shield-green.svg";
import {
  useProtectForm,
  fmt,
  DEVICE_TYPE_ICONS,
  PLATFORM_LABELS,
  parseRangeString,
  isPercentagePlan,
} from "./ProtectFormData";

// Returns mobile icon if planName contains mobile-related words, shield otherwise
const getPlanIcon = (planName) => {
  const name = (planName || "").toLowerCase();
  const mobileKeywords = [
    "mobile",
    "phone",
    "handset",
    "smartphone",
    "android",
    "ios",
  ];
  return mobileKeywords.some((kw) => name.includes(kw))
    ? MobileIcon
    : ShieldIcon;
};

// ── Main component ────────────────────────────────────────────────────────────
const ProtectForm = () => {
  const {
    brands,
    brandsLoading,
    plans,
    autoSelectedPlan,
    selectedPlan,
    setSelectedPlan,
    selectedType,
    setSelectedType,
    selectedVariant,
    setSelectedVariant,
    deviceTypes,
    platforms,
    plansLoading,
    plansError,
    form,
    errors,
    invoiceFile,
    recordingBlob,
    cameraOpen,
    setCameraOpen,
    termsChecked,
    isSubmitting,
    submitSuccess,
    submitError,
    deviceValue,
    premium,
    isFormValid,
    handleChange,
    handleInvoice,
    handleTerms,
    handleRecordingSave,
    handleActivate,
    resetForm,
    stateList,
    cityList,
  } = useProtectForm();

  const [showPlanDropdown, setShowPlanDropdown] = useState(false);

  const deviceValueRange = selectedVariant
    ? parseRangeString(selectedVariant.range)
    : null;
  const isPercentage = isPercentagePlan(selectedVariant);

  const PLATFORM_ICONS = {
    ios: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
      </svg>
    ),
    android: (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24a11.43 11.43 0 0 0-8.94 0L5.65 5.67c-.19-.28-.54-.37-.83-.22-.3.16-.42.54-.26.85l1.84 3.18C4.16 11.04 3 13.37 3 16h18c0-2.63-1.16-4.96-3.4-6.52zM7 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm10 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
      </svg>
    ),
  };

  // ── Success screen ───────────────────────────────────
  if (submitSuccess) {
    return (
      <div className="protect-page">
        <Topbar />
        <div className="success-screen">
          <div className="success-screen__icon">✅</div>
          <h2 className="success-screen__title">Enrollment Submitted!</h2>
          <p className="success-screen__msg">
            Your protection enrollment has been received. A confirmation email
            will be sent to <strong>{form.email}</strong> shortly.
          </p>
          <button className="success-screen__back" onClick={resetForm}>
            Submit Another Enrollment
          </button>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────
  return (
    <div className="protect-page">
      <Topbar />

      <main className="protect-main">
        {/* ════ LEFT PANEL ════ */}
        <div className="left-panel">
          <div className="left-panel__badge">
            <svg width="14" height="14" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
            </svg>
            TRUSTED PROTECTION PLATFORM
          </div>
          <h1 className="left-panel__headline">
            Choose your device <span>Protection Plan</span>
          </h1>
          <p className="left-panel__sub">
            Covers accidental damage, screen breakage and theft. Activates
            instantly after payment.
          </p>
          <div className="left-panel__features">
            {[
              {
                icon: "M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18",
                text: "IMEI-verified",
              },
              {
                icon: "M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z",
                text: "Encrypted payment",
              },
              {
                icon: "M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z",
                text: "24/7 claims support",
              },
            ].map(({ icon, text }) => (
              <div key={text} className="left-panel__feature-item">
                <div className="left-panel__feature-icon">
                  <svg
                    width="18"
                    height="18"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    {/* <path d={icon} /> */}
                  </svg>
                </div>
                {text}
              </div>
            ))}
          </div>
          {/* <div className="phone-placeholder">
            <div className="phone-placeholder__shield">
              <svg width="44" height="44" fill="#e2001a" viewBox="0 0 24 24">
                <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 13l-2-2 1.4-1.4.6.6 3.6-3.6L15 9l-5 5z" />
              </svg>
            </div>
            <div className="phone-placeholder__verified">✓ IMEI VERIFIED</div>
            <div className="phone-placeholder__active-label">
              PROTECTION ACTIVE
            </div>
            <div className="phone-placeholder__checks">
              <span>✓</span>
              <span>✓</span>
              <span>✓</span>
            </div>
            <div className="phone-placeholder__coverage-label">
              Screen, Battery, Theft Covered
            </div>
            <div className="phone-placeholder__coverage-badge">
              ✦ COVERAGE APPROVED
            </div>
          </div> */}
        </div>

        {/* ════ RIGHT PANEL ════ */}
        <div className="right-panel">
          <div className="enrollment-card">
            {/* Submit error banner */}
            {submitError && (
              <div className="notice notice--error" role="alert">
                <div className="notice__title">
                  <svg
                    width="18"
                    height="18"
                    fill="#e2001a"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                  </svg>
                  Submission Failed
                </div>
                <p style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
                  {submitError}
                </p>
              </div>
            )}

            {/* Notice */}
            <div className="notice">
              <div className="notice__title">
                <svg width="18" height="18" fill="#e2001a" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                </svg>
                Read Before You Start
              </div>
              <ul>
                <li>
                  Open this portal on another device to record your mobile.
                </li>
                <li>
                  Dial <strong>*#06#</strong> to display your IMEI.
                </li>
                <li>Upload a clear, continuous recording of the screen.</li>
                <li>
                  Note: <strong>Static screenshots are not accepted</strong> for
                  verification.
                </li>
              </ul>
            </div>
            {/* ══ PLAN SELECTION ══ */}
            <SectionHeader icon="shield" label="SELECT PROTECTION PLAN" />

            {plansLoading && (
              <div className="plan-loading">
                <span className="plan-loading__spinner" />
                Loading plans…
              </div>
            )}

            {plansError && (
              <div className="plan-error">&#9888; {plansError}</div>
            )}

            {!plansLoading && !plansError && (
              <>
                {/* Auto-selected plan banner */}
                {autoSelectedPlan && (
                  <div
                    className={`plan-selected-banner${
                      selectedPlan?.planId !== autoSelectedPlan?.planId
                        ? " plan-selected-banner--inactive"
                        : ""
                    }`}
                    onClick={() => setSelectedPlan(autoSelectedPlan)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) =>
                      e.key === "Enter" && setSelectedPlan(autoSelectedPlan)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={getPlanIcon(autoSelectedPlan.planName)}
                      alt="plan icon"
                      className={`plan-selected-banner__icon${
                        selectedPlan?.planId !== autoSelectedPlan?.planId
                          ? " plan-selected-banner__icon--inactive"
                          : ""
                      }`}
                    />
                    <div>
                      <div className="plan-selected-banner__name">
                        {autoSelectedPlan.planName}
                      </div>
                      <div className="plan-selected-banner__validity">
                        Valid for {autoSelectedPlan.variants[0]?.plan_Validity}{" "}
                        months
                      </div>
                    </div>
                  </div>
                )}

                {/* Additional plans grid */}
                {plans.filter((p) => p.planId !== autoSelectedPlan?.planId)
                  .length > 0 && (
                  <>
                    <div className="section-header-add">Additional Plans</div>
                    <div className="plan-grid plan-grid--others">
                      {plans
                        .filter((p) => p.planId !== autoSelectedPlan?.planId)
                        .map((plan) => {
                          const isSelected =
                            selectedPlan?.planId === plan.planId;
                          return (
                            <div
                              key={plan.planId}
                              className={`plan-card${isSelected ? " plan-card--selected" : ""}`}
                              onClick={() => setSelectedPlan(plan)}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) =>
                                e.key === "Enter" && setSelectedPlan(plan)
                              }
                            >
                              <img
                                src={getPlanIcon(plan.planName)}
                                alt="plan icon"
                                className={`plan-card__icon${isSelected ? " plan-card__icon--active" : ""}`}
                              />
                              <div className="plan-card__name">
                                {plan.planName}
                              </div>
                              {/* <div className="plan-card__validity">
                                {plan.variants[0]?.plan_Validity} months
                              </div> */}
                            </div>
                          );
                        })}
                    </div>
                  </>
                )}

                {/* Step 1 — Device type chips */}
                {selectedPlan && deviceTypes.length > 0 && (
                  <div className="device-type-strip">
                    <div className="device-type-strip__label">
                      Step 1 — Select Device Type
                    </div>
                    <div className="device-type-strip__chips">
                      {deviceTypes.map((type) => (
                        <div
                          key={type}
                          className={`s-chip${selectedType === type ? " s-chip--active" : ""}`}
                          onClick={() => setSelectedType(type)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) =>
                            e.key === "Enter" && setSelectedType(type)
                          }
                        >
                          {DEVICE_TYPE_ICONS[type] || "\ud83d\udd32"} {type}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Step 2 — Platform cards */}
                {selectedType && platforms.length > 0 && (
                  <>
                    <div
                      className="selector-strip__label"
                      style={{ marginBottom: 10 }}
                    >
                      Step 2 — Select Platform
                    </div>
                    <div className="platform-grid">
                      {platforms.map((variant) => {
                        const isSelected =
                          selectedVariant?.plan_VarriantsId ===
                          variant.plan_VarriantsId;
                        return (
                          <div
                            key={variant.plan_VarriantsId}
                            className={`platform-card${isSelected ? " platform-card--selected" : ""}`}
                            onClick={() => setSelectedVariant(variant)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) =>
                              e.key === "Enter" && setSelectedVariant(variant)
                            }
                          >
                            <div className="platform-card__platform">
                              {PLATFORM_ICONS[variant.platform]}
                              <div className="platform-card__platform">
                                {PLATFORM_LABELS[variant.platform] ||
                                  variant.platform}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}

                {errors.plan && (
                  <p
                    className="form-group__error notice-error"
                    style={{ marginBottom: 8 }}
                  >
                    {errors.plan}
                  </p>
                )}
              </>
            )}

            {/* ══ DEVICE VALUE ══ */}
            <SectionHeader
              icon="rupee"
              label="DEVICE VALUE (for premium calculation)"
            />
            <div
              className={`device-value-wrap${errors.deviceValue ? " device-value-wrap--error" : ""}`}
            >
              <span className="device-value-wrap__prefix">₹</span>
              <input
                type="number"
                name="deviceValue"
                min="0"
                placeholder={
                  deviceValueRange
                    ? `Enter value between ${fmt(deviceValueRange.min)} – ${fmt(deviceValueRange.max)}`
                    : isPercentage
                      ? `Enter device value (${selectedVariant?.dynamicPricePercentage}% of value)`
                      : "Enter invoice value of device"
                }
                value={form.deviceValue}
                onChange={handleChange}
              />
            </div>
            {!errors.deviceValue && (deviceValueRange || isPercentage) && (
              <p className="form-group__hint" style={{ marginBottom: 8 }}>
                {deviceValueRange &&
                  `Allowed range: ${fmt(deviceValueRange.min)} – ${fmt(deviceValueRange.max)}`}
                {deviceValueRange && isPercentage && " · "}
                {isPercentage &&
                  `Premium: ${selectedVariant.dynamicPricePercentage}% of device value + 18% GST`}
              </p>
            )}
            {errors.deviceValue && (
              <p className="form-group__error" style={{ marginBottom: 8 }}>
                {errors.deviceValue}
              </p>
            )}

            {/* ══ PREMIUM SUMMARY ══ */}
            {selectedVariant && (
              <div className="variant-premium">
                <div className="variant-premium__title">💰 Premium Summary</div>
                <div className="variant-premium__rows">
                  {[
                    ["Plan", selectedPlan?.planName],
                    ["Device Type", selectedVariant.type],
                    [
                      "Platform",
                      PLATFORM_LABELS[selectedVariant.platform] ||
                        selectedVariant.platform,
                    ],
                    ["Validity", selectedVariant.plan_Validity + " months"],
                    [
                      "Price Type",
                      selectedVariant.plan_Amount > 0
                        ? "Fixed"
                        : `${selectedVariant.dynamicPricePercentage}% of value`,
                    ],
                    [
                      "Device Value",
                      deviceValue > 0
                        ? fmt(deviceValue)
                        : selectedVariant.plan_Amount > 0
                          ? "N/A"
                          : "Not entered",
                    ],
                  ].map(([label, value]) => (
                    <div key={label} className="variant-premium__row">
                      <span className="variant-premium__row-label">
                        {label}
                      </span>
                      <span className="variant-premium__row-value">
                        {value || "—"}
                      </span>
                    </div>
                  ))}
                </div>
                <hr className="variant-premium__divider" />
                <div className="variant-premium__total-row">
                  <div>
                    <div className="variant-premium__total-label">
                      Total Premium Payable
                    </div>
                    {premium.total > 0 && (
                      <div className="variant-premium__gst-note">
                        Base {fmt(premium.base)} + GST 18%: {fmt(premium.gst)}
                      </div>
                    )}
                  </div>
                  {premium.total > 0 ? (
                    <div className="variant-premium__total-amount">
                      {fmt(premium.total)}
                    </div>
                  ) : (
                    <div className="variant-premium__pending">
                      Enter device value above
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ══ PERSONAL DETAILS ══ */}
            <SectionHeader icon="person" label="PERSONAL DETAILS" />
            <div className="form-grid">
              <FormInput
                name="fullName"
                placeholder="Full Name"
                value={form.fullName}
                error={errors.fullName}
                onChange={handleChange}
              />
              <FormInput
                name="email"
                placeholder="Email Address"
                value={form.email}
                error={errors.email}
                onChange={handleChange}
                type="email"
              />
              {/* Mobile — digits only, max 10 */}
              <div
                className={`form-group${errors.mobile ? " form-group--error" : ""}`}
              >
                <input
                  type="tel"
                  name="mobile"
                  placeholder="Mobile Number (10 digits)"
                  value={form.mobile}
                  onChange={handleChange}
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength={10}
                />
                {errors.mobile ? (
                  <span className="form-group__error">{errors.mobile}</span>
                ) : (
                  <span className="form-group__hint">
                    10-digit Indian mobile (starts with 6–9)
                  </span>
                )}
              </div>
              {/* PIN Code — digits only, max 6 */}
              <div
                className={`form-group${errors.zip ? " form-group--error" : ""}`}
              >
                <input
                  type="tel"
                  name="zip"
                  placeholder="PIN Code (6 digits)"
                  value={form.zip}
                  onChange={handleChange}
                  autoComplete="off"
                  inputMode="numeric"
                  maxLength={6}
                />
                {errors.zip ? (
                  <span className="form-group__error">{errors.zip}</span>
                ) : (
                  <span className="form-group__hint">
                    6-digit Indian PIN code
                  </span>
                )}
              </div>
              {/* State */}
              <div
                className={`form-group${errors.state ? " form-group--error" : ""}`}
              >
                <div className="form-group__select-wrap">
                  <select
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                  >
                    <option value="" disabled>
                      Select State
                    </option>
                    {stateList.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.state && (
                  <span className="form-group__error">{errors.state}</span>
                )}
              </div>
              {/* City / District */}
              <div
                className={`form-group${errors.city ? " form-group--error" : ""}`}
              >
                <div className="form-group__select-wrap">
                  <select
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    disabled={!form.state}
                  >
                    <option value="" disabled>
                      {form.state
                        ? "Select City / District"
                        : "Select State first"}
                    </option>
                    {cityList.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                {errors.city && (
                  <span className="form-group__error">{errors.city}</span>
                )}
              </div>
              {/* Address — full width */}
              {/* <div className="form-group form-group--full">
                <input
                  type="text"
                  name="address"
                  placeholder="Address (optional)"
                  value={form.address}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div> */}
            </div>

            {/* ══ DEVICE INFORMATION ══ */}
            <SectionHeader icon="mobile" label="DEVICE INFORMATION" />
            <div className="form-grid">
              {/* Brand — from API */}
              <div
                className={`form-group${errors.brand ? " form-group--error" : ""}`}
              >
                <div className="form-group__select-wrap">
                  <select
                    name="brand"
                    value={form.brand}
                    onChange={handleChange}
                    disabled={brandsLoading}
                  >
                    <option value="" disabled>
                      {brandsLoading ? "Loading brands…" : "Device Brand"}
                    </option>
                    {brands.map((b) => {
                      const val = typeof b === "object" ? b.value : b;
                      const key = typeof b === "object" ? b.id : b;
                      return (
                        <option key={key} value={val}>
                          {val}
                        </option>
                      );
                    })}
                  </select>
                </div>
                {errors.brand && (
                  <span className="form-group__error">{errors.brand}</span>
                )}
              </div>

              {/* IMEI */}
              <div
                className={`form-group${errors.imei ? " form-group--error" : ""}`}
              >
                <input
                  type="text"
                  name="imei"
                  placeholder="IMEI Number (15 digits)"
                  maxLength={17}
                  value={form.imei}
                  onChange={handleChange}
                  autoComplete="off"
                  inputMode="numeric"
                />
                <span
                  className="form-group__imei-icon"
                  title="Dial *#06# to get your IMEI"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="12" fill="#FACC15" />
                    <circle cx="12" cy="7" r="1.5" fill="#000" />
                    <rect
                      x="10.75"
                      y="10"
                      width="2.5"
                      height="8"
                      rx="1.25"
                      fill="#000"
                    />
                  </svg>
                </span>
                {errors.imei && (
                  <span className="form-group__error">{errors.imei}</span>
                )}
              </div>
            </div>

            {/* ══ DOCUMENTATION ══ */}
            <SectionHeader icon="doc" label="REQUIRED DOCUMENTATION" />
            <div className="upload-grid">
              {/* Invoice */}
              <div>
                <div
                  className={`upload-box${invoiceFile ? " upload-box--active" : ""}${errors.invoice ? " upload-box--error" : ""}`}
                  onClick={() => document.getElementById("invoiceFile").click()}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    e.key === "Enter" &&
                    document.getElementById("invoiceFile").click()
                  }
                >
                  <svg
                    width="32"
                    height="32"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="upload-box__title">Invoice Copy</div>
                  <div className="upload-box__sub">
                    {invoiceFile ? (
                      <span className="upload-box__status">
                        ✓ {invoiceFile.name}
                      </span>
                    ) : (
                      "PDF / JPG / PNG"
                    )}
                  </div>
                  <input
                    type="file"
                    id="invoiceFile"
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: "none" }}
                    onChange={handleInvoice}
                  />
                </div>
                {errors.invoice && (
                  <p className="form-group__error" style={{ marginTop: 4 }}>
                    {errors.invoice}
                  </p>
                )}
              </div>

              {/* Screen recording */}
              <div>
                <div
                  className={`upload-box${recordingBlob ? " upload-box--active" : ""}${errors.recording ? " upload-box--error" : ""}`}
                  onClick={() => setCameraOpen(true)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && setCameraOpen(true)}
                >
                  <svg
                    width="32"
                    height="32"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    viewBox="0 0 24 24"
                  >
                    <path d="M15 10l4.553-2.277A1 1 0 0121 8.617v6.766a1 1 0 01-1.447.894L15 14M3 8a2 2 0 012-2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                  </svg>
                  <div className="upload-box__title">Screen Recording</div>
                  <div className="upload-box__sub">
                    {recordingBlob ? (
                      <span className="upload-box__status">
                        ✓ Recording saved
                      </span>
                    ) : (
                      // "Continuous video of IMEI"
                      "Dial *#06# to display your IMEI and record video."
                    )}
                  </div>
                </div>
                {errors.recording && (
                  <p className="form-group__error" style={{ marginTop: 4 }}>
                    {errors.recording}
                  </p>
                )}
              </div>
            </div>

            {/* ══ TERMS ══ */}
            <div className="terms-wrap">
              <input
                type="checkbox"
                id="termsCheck"
                checked={termsChecked}
                onChange={handleTerms}
              />
              <label htmlFor="termsCheck">
                I agree to the{" "}
                <a href="/Terms and Conditions.html">Terms and Conditions</a>,{" "}
                {/* <a href="/privacy-policy">Privacy Policy</a>  */}
                and confirm that all provided device information is accurate to
                my best knowledge.
              </label>
            </div>
            {errors.terms && (
              <p className="form-group__error" style={{ marginTop: 4 }}>
                {errors.terms}
              </p>
            )}
          </div>
        </div>
      </main>

      {/* ── BOTTOM BAR ── */}
      <Footer
        planName={selectedPlan?.planName}
        coverage={selectedType || undefined}
        cost={premium.total > 0 ? fmt(premium.total) : undefined}
        isFormValid={isFormValid}
        isSubmitting={isSubmitting}
        onActivate={handleActivate}
      />

      {/* ── CAMERA MODAL ── */}
      <CameraModal
        isOpen={cameraOpen}
        onClose={() => setCameraOpen(false)}
        onSave={handleRecordingSave}
      />
    </div>
  );
};

// ── Reusable sub-components ──────────────────────────────────────────────────

const ICONS = {
  shield: (
    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4z" />
  ),
  rupee: (
    <path d="M13.66 7H19V5H9v2h2.64c1.83 0 3.22.97 3.62 2.5H9v2h6.26c-.45 1.41-1.8 2.5-3.6 2.5H9v1.71L14.29 21H17l-5.5-5h.16c3.1 0 5.62-1.96 6.18-4.5H19v-2h-1.01A5.74 5.74 0 0 0 17.2 7h1.8V5h-5.34z" />
  ),
  person: (
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
  ),
  mobile: (
    <path d="M17 1.01L7 1c-1.1 0-2 .9-2 2v18c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V3c0-1.1-.9-1.99-2-1.99zM17 19H7V5h10v14z" />
  ),
  doc: (
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm4 18H6V4h7v5h5v11z" />
  ),
};

const SectionHeader = ({ icon, label }) => (
  <div className="section-header">
    <div className="section-header__icon">
      <svg width="16" height="16" fill="#555" viewBox="0 0 24 24">
        {ICONS[icon]}
      </svg>
    </div>
    {label}
  </div>
);

const FormInput = ({
  name,
  placeholder,
  type = "text",
  value,
  error,
  onChange,
  hint,
}) => (
  <div className={`form-group${error ? " form-group--error" : ""}`}>
    <input
      type={type}
      name={name}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      autoComplete="off"
      inputMode={type === "tel" ? "numeric" : undefined}
    />
    {error ? (
      <span className="form-group__error">{error}</span>
    ) : (
      hint && <span className="form-group__hint">{hint}</span>
    )}
  </div>
);

export default ProtectForm;
